import { useEffect, useMemo, useState } from "react";
import {
  Search,
  ShieldCheck,
  Users,
  Tractor,
  UserCheck,
  UserX,
  RefreshCw,
} from "lucide-react";

import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const PLAN_OPTIONS = [
  { value: "beta", label: "Beta" },
  { value: "free", label: "Free" },
  { value: "pro", label: "Pro" },
  { value: "consultant", label: "Consultant" },
];

function formatDate(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return date.toLocaleDateString("en-IE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function SummaryCard({
  label,
  value,
  icon: Icon,
  description,
}) {
  return (
    <Card className="p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground font-semibold">
            {label}
          </p>

          <p className="font-display text-3xl font-extrabold text-[hsl(148,38%,16%)] mt-2">
            {value ?? 0}
          </p>

          {description && (
            <p className="text-xs text-muted-foreground mt-2">
              {description}
            </p>
          )}
        </div>

        <div className="grid h-11 w-11 place-items-center rounded-xl bg-secondary text-primary">
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </Card>
  );
}

function StatusBadge({ disabled }) {
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide ${
        disabled
          ? "bg-red-100 text-red-700"
          : "bg-green-100 text-green-700"
      }`}
    >
      {disabled ? "Disabled" : "Active"}
    </span>
  );
}

export default function AdminDashboard() {
  const [summary, setSummary] = useState(null);
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [error, setError] = useState("");

  const loadData = async () => {
    setLoading(true);
    setError("");

    try {
      const [summaryResponse, usersResponse] =
        await Promise.all([
          api.get("/admin/summary"),
          api.get("/admin/users", {
            params: {
              search: appliedSearch,
              limit: 200,
            },
          }),
        ]);

      setSummary(summaryResponse.data);
      setUsers(usersResponse.data.items || []);
      setTotalUsers(usersResponse.data.total || 0);
    } catch (err) {
      setError(
        formatApiErrorDetail(
          err.response?.data?.detail ||
            err.message
        )
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [appliedSearch]);

  const updateUser = async (
    userId,
    update
  ) => {
    setUpdatingId(userId);
    setError("");

    try {
      const { data } = await api.patch(
        `/admin/users/${userId}`,
        update
      );

      setUsers((current) =>
        current.map((item) =>
          item.id === userId
            ? {
                ...item,
                ...data,
              }
            : item
        )
      );

      const summaryResponse =
        await api.get("/admin/summary");

      setSummary(summaryResponse.data);
    } catch (err) {
      setError(
        formatApiErrorDetail(
          err.response?.data?.detail ||
            err.message
        )
      );
    } finally {
      setUpdatingId(null);
    }
  };

  const planCounts = useMemo(
    () => summary?.plans || {},
    [summary]
  );

  const handleSearch = (event) => {
    event.preventDefault();
    setAppliedSearch(search.trim());
  };

  return (
    <div
      className="space-y-6"
      data-testid="admin-dashboard"
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-primary" />

            <h1 className="font-display text-3xl font-extrabold text-[hsl(148,38%,16%)]">
              Farm Hand Admin
            </h1>
          </div>

          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Manage accounts, subscription plans and platform activity.
            Farmer passwords are never visible here.
          </p>
        </div>

        <Button
          variant="outline"
          className="rounded-full"
          onClick={loadData}
          disabled={loading}
        >
          <RefreshCw
            className={`h-4 w-4 mr-2 ${
              loading ? "animate-spin" : ""
            }`}
          />
          Refresh
        </Button>
      </div>

      {error && (
        <Card className="border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </Card>
      )}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <SummaryCard
          label="Registered users"
          value={summary?.total_users}
          icon={Users}
          description={`${summary?.new_users_7d || 0} joined in the last 7 days`}
        />

        <SummaryCard
          label="Farms"
          value={summary?.total_farms}
          icon={Tractor}
          description="Farms created across all accounts"
        />

        <SummaryCard
          label="Active accounts"
          value={summary?.active_users}
          icon={UserCheck}
          description="Accounts currently enabled"
        />

        <SummaryCard
          label="Disabled accounts"
          value={summary?.disabled_users}
          icon={UserX}
          description="Accounts blocked from access"
        />
      </div>

      <Card className="p-5">
        <h2 className="font-display font-bold text-lg text-[hsl(148,38%,16%)]">
          Subscription overview
        </h2>

        <div className="grid grid-cols-2 gap-3 mt-4 md:grid-cols-4">
          {PLAN_OPTIONS.map((plan) => (
            <div
              key={plan.value}
              className="rounded-xl border bg-secondary/30 p-4"
            >
              <p className="text-xs uppercase tracking-wider text-muted-foreground">
                {plan.label}
              </p>

              <p className="font-display text-2xl font-bold mt-1">
                {planCounts[plan.value] || 0}
              </p>
            </div>
          ))}
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="border-b p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="font-display font-bold text-lg text-[hsl(148,38%,16%)]">
                Accounts
              </h2>

              <p className="text-sm text-muted-foreground">
                {totalUsers} account
                {totalUsers === 1 ? "" : "s"} found
              </p>
            </div>

            <form
              onSubmit={handleSearch}
              className="flex w-full gap-2 lg:max-w-md"
            >
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />

                <Input
                  value={search}
                  onChange={(event) =>
                    setSearch(event.target.value)
                  }
                  placeholder="Search name or email"
                  className="pl-9"
                />
              </div>

              <Button type="submit">
                Search
              </Button>
            </form>
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Farm</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead>Usage</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">
                  Action
                </TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="py-10 text-center text-muted-foreground"
                  >
                    Loading accounts…
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="py-10 text-center text-muted-foreground"
                  >
                    No accounts found.
                  </TableCell>
                </TableRow>
              ) : (
                users.map((account) => {
                  const farmNames =
                    account.farms
                      ?.map((farm) => farm.name)
                      .filter(Boolean)
                      .join(", ") || "No farm created";

                  return (
                    <TableRow key={account.id}>
                      <TableCell>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">
                              {account.name || "Unnamed user"}
                            </p>

                            {account.role === "admin" && (
                              <span className="rounded-full bg-purple-100 px-2 py-0.5 text-[9px] font-semibold uppercase text-purple-700">
                                Admin
                              </span>
                            )}
                          </div>

                          <p className="text-xs text-muted-foreground">
                            {account.email}
                          </p>
                        </div>
                      </TableCell>

                      <TableCell>
                        <p className="max-w-[220px] truncate">
                          {farmNames}
                        </p>

                        <p className="text-xs text-muted-foreground">
                          {account.farm_count || 0} farm
                          {account.farm_count === 1
                            ? ""
                            : "s"}
                        </p>
                      </TableCell>

                      <TableCell>
                        {formatDate(
                          account.created_at
                        )}
                      </TableCell>

                      <TableCell>
                        <div className="text-xs leading-5">
                          <p>
                            {account.counts?.paddocks || 0} paddocks
                          </p>
                          <p>
                            {account.counts?.feeds || 0} feeds
                          </p>
                          <p>
                            {account.counts?.rations || 0} rations
                          </p>
                        </div>
                      </TableCell>

                      <TableCell>
                        <Select
                          value={
                            account.plan || "beta"
                          }
                          disabled={
                            updatingId === account.id
                          }
                          onValueChange={(plan) =>
                            updateUser(
                              account.id,
                              { plan }
                            )
                          }
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>

                          <SelectContent>
                            {PLAN_OPTIONS.map(
                              (plan) => (
                                <SelectItem
                                  key={plan.value}
                                  value={plan.value}
                                >
                                  {plan.label}
                                </SelectItem>
                              )
                            )}
                          </SelectContent>
                        </Select>
                      </TableCell>

                      <TableCell>
                        <StatusBadge
                          disabled={
                            account.is_disabled
                          }
                        />
                      </TableCell>

                      <TableCell className="text-right">
                        <Button
                          variant={
                            account.is_disabled
                              ? "outline"
                              : "destructive"
                          }
                          size="sm"
                          disabled={
                            updatingId === account.id ||
                            account.role === "admin"
                          }
                          onClick={() =>
                            updateUser(
                              account.id,
                              {
                                is_disabled:
                                  !account.is_disabled,
                              }
                            )
                          }
                        >
                          {account.is_disabled
                            ? "Reactivate"
                            : "Disable"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}